import React, { useState } from 'react';
import {
  Box, Container, Typography, Grid, Card, CardContent,
  Chip,
} from '@mui/material';
import {
  People, Assessment, AccountBalance, PictureAsPdf,
  WhatsApp, Email, ImportExport, Analytics,
  DirectionsCar, Inventory, Route,
  BusinessCenter, CreditCard, Notifications, Speed,
  CheckCircle,
} from '@mui/icons-material';

interface Feature {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const courierFeatures: Feature[] = [
  {
    icon: <People />,
    title: 'Gestão de estafetas',
    description: 'Adicione, faça a gestão e acompanhe todos os estafetas com atribuições a parceiros e perfis detalhados.',
  },
  {
    icon: <Assessment />,
    title: 'Relatórios semanais',
    description: 'Calcule automaticamente ganhos, comissões, taxas da plataforma e pagamentos líquidos todas as semanas.',
  },
  {
    icon: <AccountBalance />,
    title: 'Exportação SEPA',
    description: 'Gere ficheiros XML SEPA prontos para carregar no banco e pagar todos os estafetas e parceiros de uma vez.',
  },
  {
    icon: <PictureAsPdf />,
    title: 'Faturas em PDF',
    description: 'Faturas profissionais com a sua marca, geradas automaticamente para cada registo de pagamento.',
  },
  {
    icon: <WhatsApp />,
    title: 'Notificações WhatsApp',
    description: 'Envie detalhes de pagamento e faturas diretamente aos estafetas via WhatsApp com um clique.',
  },
  {
    icon: <Email />,
    title: 'Envio por correio eletrónico',
    description: 'Envie faturas e resumos de pagamento por correio eletrónico (integração SendGrid).',
  },
  {
    icon: <ImportExport />,
    title: 'Importação CSV em massa',
    description: 'Importe centenas de estafetas de uma só vez a partir de um ficheiro CSV.',
  },
  {
    icon: <Analytics />,
    title: 'Análises por parceiro',
    description: 'Acompanhe ganhos, comissões e tendências por parceiro com detalhes semanais.',
  },
];

const driverFeatures: Feature[] = [
  {
    icon: <DirectionsCar />,
    title: 'Gestão de motoristas',
    description: 'Faça a gestão de todos os motoristas, veículos e atribuições numa vista organizada.',
  },
  {
    icon: <Inventory />,
    title: 'Frota de veículos',
    description: 'Acompanhe cada veículo, valores de renda e relações com proprietários na sua frota.',
  },
  {
    icon: <Assessment />,
    title: 'Relatórios semanais de motoristas',
    description: 'Detalhe completo de ganhos: rendimento bruto, deduções e pagamento líquido por motorista.',
  },
  {
    icon: <BusinessCenter />,
    title: 'Comissões de parceiros',
    description: 'Calcule e apresente automaticamente as comissões de cada parceiro todas as semanas.',
  },
  {
    icon: <Route />,
    title: 'Rendas a proprietários',
    description: 'Acompanhe acordos de renda de veículos e pagamentos a proprietários automaticamente.',
  },
  {
    icon: <AccountBalance />,
    title: 'Pagamentos SEPA em lote',
    description: 'Pague todos os motoristas, parceiros e proprietários numa única transferência bancária SEPA.',
  },
  {
    icon: <PictureAsPdf />,
    title: 'Faturas por tipo',
    description: 'Gere faturas para motoristas, parceiros e proprietários com layouts adequados.',
  },
  {
    icon: <Speed />,
    title: 'Relatórios de utilização',
    description: 'Compreenda a utilização da frota e o desempenho dos motoristas com análises semanais.',
  },
];

const sharedFeatures = [
  { icon: <CreditCard />, title: 'Planos de subscrição flexíveis', description: 'De 20 a mais de 1000 membros — escale com preços transparentes.' },
  { icon: <Notifications />, title: 'Notificações em tempo real', description: 'Receba alertas sobre pagamentos, relatórios e atividade da equipa.' },
  { icon: <CheckCircle />, title: 'Acesso por funções', description: 'Funções de gestor, parceiro e membro da equipa com permissões detalhadas.' },
];

export default function Features() {
  const [activeTab, setActiveTab] = useState(0);

  const activeFeatures = activeTab === 0 ? courierFeatures : driverFeatures;
  const activeColor = activeTab === 0 ? '#16a34a' : '#ea580c';
  const activeBg = activeTab === 0 ? 'rgba(22,163,74,' : 'rgba(234,88,12,';

  return (
    <Box
      id="features"
      component="section"
      sx={{ py: { xs: 8, md: 12 }, background: '#ffffff' }}
    >
      <Container maxWidth="lg">
        {/* Section header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Chip
            label="FUNCIONALIDADES"
            sx={{
              mb: 2,
              background: 'rgba(30,64,175,0.08)',
              color: '#1e40af',
              fontWeight: 700,
              fontSize: '0.7rem',
              letterSpacing: '0.1em',
              height: 28,
            }}
          />
          <Typography variant="h2" sx={{ fontSize: { xs: '2rem', md: '2.75rem' }, mb: 2 }}>
            Tudo o que precisa para{' '}
            <Box component="span" sx={{
              background: 'linear-gradient(135deg, #16a34a, #1e40af, #ea580c)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}>
              gerir o negócio
            </Box>
          </Typography>
          <Typography sx={{ color: '#64748b', fontSize: '1.05rem', maxWidth: 560, mx: 'auto' }}>
            Duas plataformas especializadas, concebidas para as necessidades específicas
            de empresas de estafetas e operadores de frota.
          </Typography>
        </Box>

        {/* Tab switcher */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 5 }}>
          <Box sx={{
            background: '#f1f5f9',
            borderRadius: 3,
            p: 0.6,
            display: 'inline-flex',
          }}>
            {[
              { label: '📦  Plataforma de estafetas', color: '#16a34a' },
              { label: '🚗  Plataforma de motoristas', color: '#ea580c' },
            ].map((tab, i) => (
              <Box
                key={i}
                onClick={() => setActiveTab(i)}
                sx={{
                  px: 3,
                  py: 1.2,
                  borderRadius: 2.2,
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  transition: 'all 0.2s ease',
                  color: activeTab === i ? '#fff' : '#64748b',
                  background: activeTab === i
                    ? tab.color
                    : 'transparent',
                  boxShadow: activeTab === i
                    ? `0 4px 12px ${tab.color}40`
                    : 'none',
                }}
              >
                {tab.label}
              </Box>
            ))}
          </Box>
        </Box>

        {/* Feature cards grid */}
        <Grid container spacing={2.5} sx={{ mb: 8 }}>
          {activeFeatures.map((feature, i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Card
                sx={{
                  height: '100%',
                  border: '1px solid #e2e8f0',
                  boxShadow: 'none',
                  transition: 'all 0.25s ease',
                  '&:hover': {
                    borderColor: activeColor,
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 32px ${activeBg}0.1)`,
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 2,
                      background: `${activeBg}0.1)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2,
                      color: activeColor,
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', mb: 1, color: '#0f172a' }}>
                    {feature.title}
                  </Typography>
                  <Typography sx={{ fontSize: '0.82rem', color: '#64748b', lineHeight: 1.6 }}>
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Shared features strip */}
        <Box sx={{
          background: 'linear-gradient(135deg, #0d1b2a 0%, #1a2f4a 100%)',
          borderRadius: 3,
          p: { xs: 3, md: 5 },
        }}>
          <Typography
            variant="h5"
            sx={{ color: '#fff', textAlign: 'center', mb: 4, fontWeight: 700 }}
          >
            Incluído em ambas as plataformas
          </Typography>
          <Grid container spacing={3}>
            {sharedFeatures.map((feat, i) => (
              <Grid item xs={12} md={4} key={i}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <Box sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    background: 'rgba(255,255,255,0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#94d9a2',
                    flexShrink: 0,
                  }}>
                    {feat.icon}
                  </Box>
                  <Box>
                    <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: '0.9rem', mb: 0.5 }}>
                      {feat.title}
                    </Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.82rem', lineHeight: 1.6 }}>
                      {feat.description}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>
    </Box>
  );
}
